// Mirror of Shared/Calculators/TileCalculator.cs (compute + present + field data).
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "roomWidthFt", kind: "number", default: 12, min: 0.5, max: 200 },
  { key: "roomLengthFt", kind: "number", default: 14, min: 0.5, max: 200 },
  {
    key: "tileSize",
    kind: "select",
    defaultChoice: "12x12",
    choices: [
      { value: "12x12", set: { tileAreaSqft: 1.0, boxCoverageSqft: 15.0 } },
      { value: "18x18", set: { tileAreaSqft: 2.25, boxCoverageSqft: 13.5 } },
      { value: "12x24", set: { tileAreaSqft: 2.0, boxCoverageSqft: 16.0 } },
      { value: "24x24", set: { tileAreaSqft: 4.0, boxCoverageSqft: 16.0 } },
      { value: "6x24", set: { tileAreaSqft: 1.0, boxCoverageSqft: 8.0 } },
    ],
  },
  { key: "wastePct", kind: "number", default: 10, min: 0, max: 30 },
  { key: "pricePerSqft", kind: "number", default: 3.5, min: 0, max: 100 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const w = F.get(i, "roomWidthFt");
  const l = F.get(i, "roomLengthFt");
  const tileArea = F.get(i, "tileAreaSqft");
  const boxCov = F.get(i, "boxCoverageSqft");
  const waste = F.get(i, "wastePct");
  const price = F.get(i, "pricePerSqft");

  const area = w * l;
  const areaWithWaste = area * (1 + waste / 100);
  const tiles = tileArea > 0 ? F.ceilUnits(areaWithWaste / tileArea) : 0;
  const boxes = boxCov > 0 ? F.ceilUnits(areaWithWaste / boxCov) : 0;
  const boughtArea = boxes * boxCov;
  const cost = boughtArea * price;

  return {
    area: F.round(area, 2),
    areaWithWaste: F.round(areaWithWaste, 2),
    tiles,
    boxes,
    boughtArea: F.round(boughtArea, 2),
    cost: F.round(cost, 2),
  };
}

function present(i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const waste = F.get(i, "wastePct");
  const boxes = F.get(r, "boxes");
  return [
    { label: "Floor area", value: `${F.dec(F.get(r, "area"))} sq ft` },
    { label: `With ${F.dec(waste, 0)}% waste`, value: `${F.dec(F.get(r, "areaWithWaste"))} sq ft` },
    { label: "Tiles needed", value: F.intStr(F.get(r, "tiles")) },
    { label: "Boxes to buy", value: `${F.intStr(boxes)} ${F.plural(boxes, "box", "boxes")}`, highlight: true },
    { label: "Est. materials cost", value: F.usd(F.get(r, "cost")), sub: "rough — tile only, excludes thin-set, grout & labor" },
  ];
}

export const tile: CalcDef = { slug: "tile-calculator", fields, compute, present };
